import { useEffect, useState } from "react";
import Icon from "../../components/Icon";
import LoadingBlock from "../../components/LoadingBlock";
import EmptyState from "../../components/EmptyState";
import Drawer from "../../components/Drawer";
import { useApi } from "../../hooks/useApi";
import {
  listClientPrefixes,
  createClientPrefix,
  updateClientPrefix,
  deleteClientPrefix,
} from "../../api/clientPrefixService";
import { showApiError, showToast } from "../../utils/toast";

const sample = (code, padding) => `${code}${String(1).padStart(padding || 4, "0")}`;

export default function ClientPrefixesPage() {
  const [drawer, setDrawer] = useState(null); // null | { mode: 'create' } | { mode: 'edit', prefix }
  const prefixes = useApi(() => listClientPrefixes());
  const rows = Array.isArray(prefixes.data?.data) ? prefixes.data.data : [];

  const onDelete = async (p) => {
    if (!window.confirm(`Delete prefix "${p.code}"? Existing client IDs are not affected.`)) return;
    try {
      await deleteClientPrefix(p.id);
      showToast("success", "Prefix deleted");
      prefixes.reload();
    } catch (err) {
      showApiError(err);
    }
  };

  return (
    <>
      <div className="page-head">
        <div>
          <h1 className="page-title">Client ID Prefixes</h1>
          <div className="page-sub">Reusable prefixes that auto-number new client IDs</div>
        </div>
        <div className="page-actions">
          <button className="btn btn-primary" onClick={() => setDrawer({ mode: "create" })}>
            <Icon name="plus" size={14} /> New prefix
          </button>
        </div>
      </div>

      {prefixes.loading ? (
        <LoadingBlock rows={2} height={80} />
      ) : rows.length === 0 ? (
        <EmptyState
          icon="list"
          title="No prefixes yet"
          hint="Create a prefix (e.g. SCA-NR-) so new client IDs auto-increment."
        />
      ) : (
        <div className="card">
          <div className="card-body no-pad">
            <table className="table">
              <thead>
                <tr>
                  <th>Prefix</th>
                  <th style={{ width: 120 }}>Padding</th>
                  <th>Example</th>
                  <th style={{ width: 110, textAlign: "right" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((p) => (
                  <tr key={p.id}>
                    <td className="t-mono fw-600">{p.code}</td>
                    <td className="t-muted">{p.padding}</td>
                    <td className="t-mono t-muted">{sample(p.code, p.padding)}</td>
                    <td>
                      <div className="row gap-sm" style={{ justifyContent: "flex-end" }}>
                        <button className="icon-btn" title="Edit" onClick={() => setDrawer({ mode: "edit", prefix: p })}>
                          <Icon name="edit" size={13} />
                        </button>
                        <button
                          className="icon-btn"
                          title="Delete"
                          style={{ color: "var(--danger)" }}
                          onClick={() => onDelete(p)}
                        >
                          <Icon name="trash" size={13} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <PrefixDrawer
        open={!!drawer}
        mode={drawer?.mode}
        prefix={drawer?.prefix}
        onClose={() => setDrawer(null)}
        onSaved={() => {
          setDrawer(null);
          prefixes.reload();
        }}
      />
    </>
  );
}

function PrefixDrawer({ open, mode, prefix, onClose, onSaved }) {
  const [code, setCode] = useState("");
  const [padding, setPadding] = useState(4);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setCode(mode === "edit" && prefix ? prefix.code : "");
    setPadding(mode === "edit" && prefix ? prefix.padding : 4);
    setErrors({});
  }, [open, mode, prefix]);

  const submit = async () => {
    setErrors({});
    setSaving(true);
    try {
      const payload = { code: code.trim(), padding: Number(padding) || 4 };
      if (mode === "edit") {
        await updateClientPrefix(prefix.id, payload);
        showToast("success", "Prefix updated");
      } else {
        await createClientPrefix(payload);
        showToast("success", "Prefix created");
      }
      onSaved?.();
    } catch (err) {
      if (err?.data?.errors) setErrors(err.data.errors);
      showApiError(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={mode === "edit" ? `Edit ${prefix?.code || "prefix"}` : "New prefix"}
      footer={
        <>
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={submit} disabled={saving || !code.trim()}>
            {saving ? "Saving…" : mode === "edit" ? "Save changes" : "Create"}
          </button>
        </>
      }
    >
      <div className="col gap-md">
        <div className="field">
          <label className="field-label">Prefix</label>
          <input
            className="input"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="SCA-NR-"
          />
          <div className="field-hint">The literal start of the client ID, including any separator (e.g. SCA-NR-).</div>
          {errors.code?.[0] && <div className="field-error">{errors.code[0]}</div>}
        </div>
        <div className="field">
          <label className="field-label">Number padding</label>
          <input
            type="number"
            min="1"
            max="12"
            className="input"
            style={{ maxWidth: 120 }}
            value={padding}
            onChange={(e) => setPadding(e.target.value)}
          />
          <div className="field-hint">
            Zero-pad width for the first ID. Example: <span className="t-mono">{sample(code || "SCA-NR-", Number(padding) || 4)}</span>
          </div>
          {errors.padding?.[0] && <div className="field-error">{errors.padding[0]}</div>}
        </div>
      </div>
    </Drawer>
  );
}
